import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { isFunction, omitBy, isNil, compact } from 'lodash';
import calculateSeek from 'utils/calculate-seek';
import VIDEO_EVENTS from 'constants/video-events';
import PLAYER_TYPES from 'constants/player-types';
import GlobalLoader from 'components/Loader';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { pagesActions } from 'store/pages';
import { epgActions } from 'store/epg';
import { DetailsObjectType } from 'store/pages/loaders/loader-types';
import useDocumentVisibilityChange from 'hooks/useDocumentVisibilityChange';
import { QueryParamsType } from 'store/player/types';
import AbstractPlayerInterface from './VendorPlayers/HTML5PlayerInterface';
import { HLSPlayer } from './VendorPlayers';
import useVideoTarget from './useVideoTarget';
import useIdleTimeout from './useIdleTimeout';
import useMetadata from './useMetadata';
import useLoadResource from './useLoadResource';
import useOnKeydown from './useOnKeydown';
import { Controls, ButtonsSet, ProgressBar } from './Controls';
import Episodes from './Episodes';
import { StyledPlayerWrapper } from './PlayerStyledComponents';
import { ACTION_TYPES } from './playerConstants';
import Platform from 'platform';

export interface IPlayerProps {
  playerType: string;
  queryParams: QueryParamsType;
  navigation: {
    prev: { id: number } | null;
    next: { id: number } | null;
  };
  object: DetailsObjectType;
  isObjectLoading?: boolean;
  withControls?: boolean;
  width?: number;
  height?: number;
}

const SEEK_TIMEOUT = 1000;
const PROGRESS_WIDTH = 1480;

const playersByPlatform = {
  Browser: HLSPlayer,
};

let seekTimeout = null;

const Player: React.FC<IPlayerProps> = (props) => {
  const { playerType, queryParams, withControls = true, width = 1920, height = 1080 } = props;
  const { object, isObjectLoading, navigation } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  // todo: add platform selection
  const VendorPlayer = playersByPlatform['Browser'];
  const playerRef = useRef<AbstractPlayerInterface>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [seekPending, setSeekPending] = useState(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isEpisodesFocused, setIsEpisodesFocused] = useState(false);
  // TODO: select from object
  const startFrom = 0;
  const isIdleShouldBeEnabled = !isEpisodesFocused;

  const idle = useIdleTimeout(isIdleShouldBeEnabled);
  const videoTarget = useVideoTarget({
    playerType,
    queryParams,
    object,
    isObjectLoading,
  });
  const { title, description, vastGenre, vastRating, objectTitle = null } = useMetadata(
    object,
    videoTarget,
  );
  const { resource } = useLoadResource(videoTarget);

  const isContentReadyToPlay = !!resource && !!resource.playback_url;

  const modifySrcWithVASTParams = useCallback(
    (resource) => {
      if (!isContentReadyToPlay) return null;

      const VASTParams = {
        'ads.h': height,
        'ads.w': width,
        'ads.app_platform': 'sstv', //Platform.getVASTAppPlatformName(),
        'ads.app_name': 'MOMETU', //Переписать на значение из конфига потом
        'ads.rating': vastRating || 'TV14',
        'ads.us_privacy': '1N-N',
        'ads.app_ver': process.env.REACT_APP_VERSION,
        'ads.did': Platform.getUID(),
        'ads.content_genre': vastGenre,
        'ads.channel_name': playerType === PLAYER_TYPES.CHANNEL ? objectTitle : null,
        'ads.content_series': playerType === PLAYER_TYPES.SERIES ? objectTitle : null,
      };

      const vastParamsString = Object.keys(omitBy(VASTParams, isNil))
        .map((k) => `${k}=${encodeURIComponent(VASTParams[k])}`)
        .join('&');
      const joinSymbol = resource.playback_url.indexOf('?') === -1 ? '?' : '&';
      return resource.playback_url + joinSymbol + vastParamsString;
    },
    [object],
  );

  const src = isContentReadyToPlay ? modifySrcWithVASTParams(resource) : '';

  const logo = useMemo(() => {
    if (playerType !== PLAYER_TYPES.CHANNEL) return null;
    return object?.resources.find(({ type }) => type === 'picture')?.url;
  }, [object?.resources, playerType]);

  const hasEpisodes = playerType === PLAYER_TYPES.SERIES && !!object?.series_seasons;

  // CLICK HANDLERS
  const onClickPlay = useCallback(() => {
    const player = playerRef.current;
    player && player.play();
  }, []);

  const onClickPause = useCallback(() => {
    const player = playerRef.current;

    player && player.pause();
  }, []);

  const seekAfterTimeout = useCallback(() => {
    clearTimeout(seekTimeout);
    seekTimeout = setTimeout(() => {
      const player = playerRef.current;
      player.setCurrentTime(seekPending);
    }, SEEK_TIMEOUT);
  }, [seekPending]);

  const onClickRewind = useCallback(() => {
    const newSeek = (seekPending !== null ? seekPending : currentTime) - calculateSeek(duration);

    setSeekPending(newSeek >= 0 ? newSeek : 0);

    seekAfterTimeout();
  }, [currentTime, duration, seekAfterTimeout, seekPending]);

  const onClickFF = useCallback(() => {
    const newSeek = (seekPending || currentTime) + calculateSeek(duration);

    setSeekPending(newSeek <= duration ? newSeek : duration);

    seekAfterTimeout();
  }, [currentTime, duration, seekAfterTimeout, seekPending]);

  const onClickPrev = useCallback(() => {
    if (!navigation.prev) return;

    if (playerType === PLAYER_TYPES.SERIES) {
      history.replace(`/player/series/${object.id}/${navigation.prev.id}`);
    } else if (playerType === PLAYER_TYPES.CHANNEL) {
      history.replace(`/player/channel/${navigation.prev.id}`);
    }
  }, [navigation.prev, playerType, history, object]);

  const onClickNext = useCallback(() => {
    if (!navigation.next) return;

    if (playerType === PLAYER_TYPES.SERIES) {
      history.replace(`/player/series/${object.id}/${navigation.next.id}`);
    } else if (playerType === PLAYER_TYPES.CHANNEL) {
      history.replace(`/player/channel/${navigation.next.id}`);
    }
  }, [navigation.next, playerType, history, object]);

  const changeChannel = useCallback(
    (nextChannel) => {
      // This logic here because "connected-react-router" performs "replace" action too slow
      if (nextChannel) history.replace(`/player/channel/${nextChannel.id}`);
    },
    [history],
  );

  const onFavoriteClick = useCallback(() => {
    dispatch(pagesActions.toggleFavoriteStatus(object));
  }, [dispatch, object]);

  const onControlClick = useCallback(
    (type: keyof typeof ACTION_TYPES) => {
      const actionsTyType = {
        [ACTION_TYPES.PAUSE]: onClickPause,
        [ACTION_TYPES.PLAY]: onClickPlay,
        [ACTION_TYPES.FF]: onClickFF,
        [ACTION_TYPES.RW]: onClickRewind,
        [ACTION_TYPES.PREV]: onClickPrev,
        [ACTION_TYPES.NEXT]: onClickNext,
        [ACTION_TYPES.FAVORITE]: onFavoriteClick,
        [ACTION_TYPES.CH_UP]: () => changeChannel(navigation.prev),
        [ACTION_TYPES.CH_DOWN]: () => changeChannel(navigation.next),
        [ACTION_TYPES.EPG]: () => dispatch(epgActions.show()),
        [ACTION_TYPES.STOP]: () => history.goBack(),
      };

      const action = actionsTyType[type];

      if (isFunction(action)) action();
    },
    [
      onClickPause,
      onClickPlay,
      onClickFF,
      onClickRewind,
      onClickPrev,
      onClickNext,
      onFavoriteClick,
      changeChannel,
      navigation.prev,
      navigation.next,
      dispatch,
      history,
    ],
  );

  const onEpisodesEnter = useCallback(
    (item) => {
      // return focus to the top
      SpatialNavigationWrapper.focus('@BUTTONS_SET');
      history.replace(`/player/series/${object.id}/${item.id}`);
    },
    [history, object],
  );

  const onButtonsFocus = useCallback(() => setIsEpisodesFocused(false), []);
  const onEpisodesFocus = useCallback(() => setIsEpisodesFocused(true), []);

  // EVENT HANDLERS
  const onTimeUpdate = useCallback(() => {
    const player = playerRef.current;
    const currentTime = player.getCurrentTime();

    if (currentTime === undefined || Number.isNaN(currentTime)) return;

    setCurrentTime(currentTime);
  }, []);

  const onVideoEvent = useCallback(
    (type) => {
      const player = playerRef.current;

      const ActionByEventType = {
        [VIDEO_EVENTS.TIME_UPDATE]: onTimeUpdate,
        [VIDEO_EVENTS.LOADED_METADATA]: () => setDuration(player.getDuration()),
        [VIDEO_EVENTS.PAUSE]: () => {
          // Prevent unnecessary "stop" event
          if (!isPlaying) return;
          setIsPlaying(false);
        },
        [VIDEO_EVENTS.WAITING]: () => setIsWaiting(true),
        [VIDEO_EVENTS.LOAD_START]: () => setIsWaiting(true),
        [VIDEO_EVENTS.SEEKING]: () => setIsSeeking(true),
        [VIDEO_EVENTS.SEEKED]: () => setIsSeeking(false),
        [VIDEO_EVENTS.PLAYING]: () => {
          setIsPlaying(true);
          setIsWaiting(false);
        },
        [VIDEO_EVENTS.CAN_PLAY]: onClickPlay,
      };

      const action = ActionByEventType[type];

      if (isFunction(action)) action();
    },
    [isPlaying, onClickPlay, onTimeUpdate],
  );

  const onVisibilityChange = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    window.document.hidden ? onClickPause() : onClickPlay();
  }, [onClickPause, onClickPlay]);

  useOnKeydown(onControlClick);

  useDocumentVisibilityChange(onVisibilityChange);

  useEffect(() => {
    if (!isSeeking) setSeekPending(null);
  }, [isSeeking]);

  return (
    <StyledPlayerWrapper>
      {isContentReadyToPlay && VendorPlayer && (
        <VendorPlayer
          src={src}
          ref={playerRef}
          playerType={playerType}
          onVideoEvent={onVideoEvent}
          startFrom={startFrom}
          width={width}
          height={height}
        />
      )}
      {withControls && (
        <Controls
          title={title}
          description={description}
          logo={logo}
          idle={idle}
          bottomOffset={isEpisodesFocused && 755}
          showHeader={!isEpisodesFocused}
          darkOverlay={isEpisodesFocused}
        >
          <ButtonsSet
            sectionName="BUTTONS_SET"
            onClick={onControlClick}
            onSectionFocus={onButtonsFocus}
            isPrevAvailable={!!navigation.prev}
            isNextAvailable={!!navigation.next}
            favorite={object?.favorite}
            isPlaying={isPlaying}
            playerType={playerType}
          />
          {playerType !== PLAYER_TYPES.CHANNEL && (
            <ProgressBar
              width={PROGRESS_WIDTH}
              seekPending={seekPending}
              currentTime={currentTime}
              duration={duration}
            />
          )}
          {hasEpisodes && (
            <Episodes
              seasons={object.series_seasons}
              onEnter={onEpisodesEnter}
              onFocus={onEpisodesFocus}
              focusedEpisodeId={videoTarget.id}
            />
          )}
        </Controls>
      )}
      {(isWaiting || isObjectLoading) && <GlobalLoader cleanBackground={true} />}
    </StyledPlayerWrapper>
  );
};

export default Player;
